import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/Sidebar";
import api from "../../api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { ArrowLeft, Upload, User } from "lucide-react";

const Field = ({ label, id, ...props }) => (
  <div>
    <Label htmlFor={id} className="text-slate-700 font-medium text-sm">{label}</Label>
    <Input id={id} className="mt-1.5 border-slate-200 focus-visible:ring-[#38b6e8]" {...props} />
  </div>
);

export default function AddEmployee() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const photoRef = useRef();

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    aadhar_no: "", pan_no: "", address: "", job_role: "",
    joining_date: "", password: "", photo: null,
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);

  useEffect(() => {
    if (isEdit) {
      api.get(`/employees/${id}`)
        .then((r) => {
          const { photo, ...rest } = r.data;
          setForm((prev) => ({ ...prev, ...rest, photo: photo || null }));
        })
        .catch(() => toast.error("Failed to load employee"))
        .finally(() => setFetching(false));
    }
  }, [id, isEdit]);

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setForm((prev) => ({ ...prev, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEdit) {
        const { password, email, ...updateFields } = form;
        await api.put(`/employees/${id}`, updateFields);
        toast.success("Employee updated successfully");
      } else {
        if (!form.password) { toast.error("Password is required"); setLoading(false); return; }
        await api.post("/employees", form);
        toast.success("Employee created successfully");
      }
      navigate("/admin/employees");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return <Layout><div className="p-8 text-slate-400">Loading...</div></Layout>;
  }

  return (
    <Layout>
      <div className="p-8 page-enter max-w-4xl">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/admin/employees")}
            className="p-2 rounded-lg hover:bg-slate-200 transition-colors text-slate-600"
            data-testid="back-btn"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{isEdit ? "Edit Employee" : "Add New Employee"}</h1>
            <p className="text-slate-500 text-sm mt-0.5">{isEdit ? "Update employee information" : "Fill in the details to onboard a new employee"}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Photo card */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex flex-col items-center gap-4">
              <div className="h-28 w-28 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden">
                {form.photo ? (
                  <img src={form.photo} alt="Preview" className="h-full w-full object-cover" />
                ) : (
                  <User size={40} className="text-slate-300" />
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => photoRef.current?.click()}
                className="gap-2 border-slate-200 text-slate-600 hover:border-[#38b6e8] hover:text-[#38b6e8]"
                data-testid="upload-photo-btn"
              >
                <Upload size={14} /> Upload Photo
              </Button>
              <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
              <p className="text-xs text-slate-400 text-center">JPG, PNG up to 2MB</p>
            </div>

            {/* Form fields */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h3 className="font-semibold text-slate-800 mb-5">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <Field label="First Name" id="first_name" value={form.first_name} onChange={set("first_name")} placeholder="John" required data-testid="input-first-name" />
                <Field label="Last Name" id="last_name" value={form.last_name} onChange={set("last_name")} placeholder="Doe" required data-testid="input-last-name" />
                {!isEdit && <Field label="Email" id="email" type="email" value={form.email} onChange={set("email")} placeholder="john@company.com" required data-testid="input-email" />}
                <Field label="Phone" id="phone" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" required data-testid="input-phone" />
                <Field label="Aadhar Number" id="aadhar_no" value={form.aadhar_no} onChange={set("aadhar_no")} placeholder="1234 5678 9012" required data-testid="input-aadhar" />
                <Field label="PAN Number" id="pan_no" value={form.pan_no} onChange={set("pan_no")} placeholder="ABCDE1234F" required data-testid="input-pan" />
              </div>

              <h3 className="font-semibold text-slate-800 mb-5">Employment Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <Field label="Job Role / Designation" id="job_role" value={form.job_role} onChange={set("job_role")} placeholder="Software Engineer" required data-testid="input-job-role" />
                <Field label="Joining Date" id="joining_date" type="date" value={form.joining_date} onChange={set("joining_date")} required data-testid="input-joining-date" />
                {!isEdit && (
                  <Field label="Initial Password" id="password" type="password" value={form.password} onChange={set("password")} placeholder="Set login password" required data-testid="input-password" />
                )}
              </div>

              <div>
                <Label htmlFor="address" className="text-slate-700 font-medium text-sm">Address</Label>
                <Textarea
                  id="address"
                  value={form.address}
                  onChange={set("address")}
                  placeholder="Full residential address"
                  className="mt-1.5 border-slate-200 focus-visible:ring-[#38b6e8] resize-none"
                  rows={3}
                  required
                  data-testid="input-address"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/employees")}
              className="border-slate-200 text-slate-600"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#1a2332] hover:bg-[#2c3e50] text-white px-8"
              data-testid="save-employee-btn"
            >
              {loading ? "Saving..." : isEdit ? "Save Changes" : "Add Employee"}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
